import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer,
  ReferenceLine, ComposedChart, Legend as RechartsLegend, Line, Customized
} from 'recharts';
import PriceCap from './PriceCap';
import FuturesChart from './FuturesChart';
import './Wholesale.css';

const PROFILE_RANGE_OPTIONS = [
  { value: '1d', label: '1 day', shortLabel: '1D' },
  { value: '1w', label: '1 week', shortLabel: '1W' },
  { value: '1m', label: '1 month', shortLabel: '1M' },
  { value: '1y', label: '1 year', shortLabel: '1Y' },
  { value: '5y', label: '5 years', shortLabel: '5Y' },
  { value: 'iran', label: 'Since Iran', shortLabel: 'Since Iran' },
  { value: 'ukraine', label: 'Ukraine 22/23', shortLabel: 'Ukraine 22/23' },
  { value: 'noncrisis', label: 'Non-crisis', shortLabel: 'Non-crisis' },
];

const SEASON_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'winter', label: 'Winter' },
  { value: 'summer', label: 'Summer' },
];

const AVERAGE_LINES = [
  { key: '5y', label: '5yr avg', color: '#3498db' },
  { key: 'noncrisis', label: 'Non-crisis avg', color: '#27ae60' },
  { key: 'iran', label: 'Since Iran', color: '#e67e22' },
  { key: 'ukraine', label: 'Ukraine crisis', color: '#e74c3c' },
];

// CfD strike prices in £/MWh
// price2012 = initial strike price in 2012 terms (VWAvg)
// price2025 = current adjusted strike price in £2025 (from LCCC/published data, includes TLM(D)/BSUoS adjustments)
// awardYear = year the CfD was awarded (line starts from Apr of this year)
const CFD_STRIKE_PRICES = {
  offshoreWind: [
    { round: 'IC',  price2012: 144.23, price2025: 206.05, awardYear: 2014 },
    { round: 'AR1', price2012: 117.75, price2025: 167.81, awardYear: 2014 },
    { round: 'AR2', price2012: 62.15,  price2025: 86.77,  awardYear: 2017 },
    { round: 'AR3', price2012: 40.68,  price2025: 54.90,  awardYear: 2019 },
    { round: 'AR4', price2012: 37.72,  price2025: 49.26,  awardYear: 2021 },
    // AR5: no viable allocation (all returned)
    { round: 'AR6', price2012: 67.40,  price2025: 97.28,  awardYear: 2024 },
    { round: 'AR7', price2012: 67.28,  price2025: 97.11,  awardYear: 2026 },
  ],
  onshoreWind: [
    { round: 'AR1', price2012: 82.03,  price2025: 116.90, awardYear: 2014 },
    // AR2-AR3: excluded from eligibility
    { round: 'AR4', price2012: 40.78,  price2025: 53.26,  awardYear: 2021 },
    { round: 'AR5', price2012: 43.66,  price2025: 57.02,  awardYear: 2023 },
    { round: 'AR6', price2012: 52.29,  price2025: 75.46,  awardYear: 2024 },
    { round: 'AR7', price2012: 51.85,  price2025: 74.83,  awardYear: 2026 },
  ],
  solar: [
    { round: 'AR1', price2012: 79.23,  price2025: 112.91, awardYear: 2014 },
    // AR2-AR3: no allocation
    { round: 'AR4', price2012: 45.99,  price2025: 60.06,  awardYear: 2021 },
    { round: 'AR5', price2012: 47.00,  price2025: 61.38,  awardYear: 2023 },
    { round: 'AR6', price2012: 50.07,  price2025: 72.25,  awardYear: 2024 },
    { round: 'AR7', price2012: 46.82,  price2025: 67.56,  awardYear: 2026 },
  ],
  nuclear: [
    { round: 'HPC', price2012: 89.50,  price2025: 127.00, awardYear: 2016 },
  ],
};

const CFD_TECH_COLORS = {
  offshoreWind: '#58d68d',
  onshoreWind: '#85c1e9',
  solar: '#f4d03f',
  nuclear: '#e8a0bf',
};

const CFD_TECH_LABELS = {
  offshoreWind: 'Offshore Wind',
  onshoreWind: 'Onshore Wind',
  solar: 'Solar',
  nuclear: 'Nuclear',
};

const DEFAULT_RANGE = { key: 'custom', days: null, startDate: '2026-01-01' };

const AXIS_TICK_COLOR = '#555';
const EVENT_LABEL_SIZE = 13;

function shouldAggregateWeekly(range) {
  if (range.days === Infinity) return true;
  if (range.days > 365) return true;
  if (range.key === 'custom' && range.startDate) {
    const start = new Date(range.startDate);
    const end = range.endDate ? new Date(range.endDate) : new Date();
    return (end - start) / (1000 * 60 * 60 * 24) > 365;
  }
  return false;
}

const GOOGLE_SHEET_URL = 'https://docs.google.com/spreadsheets/d/1JjQrNv_1SG05Vb99gOGoc9IIz9x5lNGn4GkvFozydlk';

const TIME_RANGES = [
  { key: '1w', label: '1W', days: 7 },
  { key: '1m', label: '1M', days: 30 },
  { key: '3m', label: '3M', days: 90 },
  { key: '6m', label: '6M', days: 180 },
  { key: '1y', label: '1Y', days: 365 },
  { key: 'all', label: 'All', days: Infinity },
  { key: 'custom', label: 'Custom', days: null },
];

const EVENT_LINES = [
  { date: '2026-02-28', label: 'Iran war' },
  { date: '2022-02-24', label: 'Invasion of Ukraine' },
];

function formatDate(dateStr) {
  const d = new Date(dateStr);
  // Hourly timestamps contain 'T'
  if (dateStr && dateStr.includes('T')) {
    return d.toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
  }
  const now = new Date();
  const monthsDiff = Math.abs((now.getFullYear() - d.getFullYear()) * 12 + now.getMonth() - d.getMonth());
  if (monthsDiff > 10) {
    return d.toLocaleDateString('en-GB', { month: 'short', year: '2-digit' });
  }
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

function formatDateLong(dateStr) {
  const d = new Date(dateStr);
  if (dateStr && dateStr.includes('T')) {
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
      + ' ' + d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  }
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

// Process gas data
function processGasDaily(data) {
  return data
    .map(row => {
      const date = row['Date'];
      if (!date) return null;
      const ons = parseFloat(row['Price (£/MWh) [ONS SAP]']);
      const eex = parseFloat(row['Price (£/MWh) [EEX]']);
      const smart = parseFloat(row['Price (£/MWh) [Smart Energy]']);
      const realOns = parseFloat(row['Real Price (£/MWh) [ONS SAP]']);
      const realEex = parseFloat(row['Real Price (£/MWh) [EEX]']);
      const realSmart = parseFloat(row['Real Price (£/MWh) [Smart Energy]']);
      const price = !isNaN(eex) ? eex : !isNaN(ons) ? ons : !isNaN(smart) ? smart : null;
      const realPrice = !isNaN(realEex) ? realEex : !isNaN(realOns) ? realOns : !isNaN(realSmart) ? realSmart : null;
      return {
        date,
        ons: isNaN(ons) ? null : ons,
        eex: isNaN(eex) ? null : eex,
        smart: isNaN(smart) ? null : smart,
        price,
        realPrice: realPrice ?? price,
      };
    })
    .filter(d => d !== null && d.price !== null)
    .sort((a, b) => a.date.localeCompare(b.date));
}

// Combine HH data (averaged to daily) with pre-aggregated daily data
function combineElecData(hh, daily) {
  const avg = arr => arr.length > 0 ? +(arr.reduce((a, b) => a + b, 0) / arr.length).toFixed(2) : null;

  const hhByDate = {};
  (hh || []).forEach(row => {
    if (!row.date) return;
    if (!hhByDate[row.date]) hhByDate[row.date] = { lccc: [], elexon: [], realLccc: [], realElexon: [] };
    if (row.lccc != null) hhByDate[row.date].lccc.push(row.lccc);
    if (row.elexon != null) hhByDate[row.date].elexon.push(row.elexon);
    if (row.realLccc != null) hhByDate[row.date].realLccc.push(row.realLccc);
    if (row.realElexon != null) hhByDate[row.date].realElexon.push(row.realElexon);
  });

  const hhDaily = Object.entries(hhByDate).map(([date, d]) => ({
    date,
    lccc: avg(d.lccc), elexon: avg(d.elexon),
    realLccc: avg(d.realLccc), realElexon: avg(d.realElexon),
  }));

  const byDate = {};
  (daily || []).forEach(d => { byDate[d.date] = d; });
  hhDaily.forEach(d => { byDate[d.date] = d; });

  return Object.values(byDate)
    .filter(d => d.lccc !== null || d.elexon !== null)
    .map(d => ({ ...d, price: d.lccc ?? d.elexon, realPrice: d.realLccc ?? d.realElexon }))
    .sort((a, b) => a.date.localeCompare(b.date));
}

function filterByRange(data, range) {
  if (range.days === Infinity) return data;
  if (range.key === 'custom' && range.startDate) {
    return data.filter(d => d.date >= range.startDate && (!range.endDate || d.date <= range.endDate));
  }
  let cutoffStr;
  if (range.startDate) {
    cutoffStr = range.startDate;
  } else if (range.days) {
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - range.days);
    cutoffStr = cutoff.toISOString().split('T')[0];
  } else {
    return data;
  }
  return data.filter(d => d.date >= cutoffStr);
}

function mergeForOverlay(elecDaily, gasDaily) {
  const merged = {};
  elecDaily.forEach(d => {
    merged[d.date] = { date: d.date, elec: d.price };
  });
  gasDaily.forEach(d => {
    if (!merged[d.date]) merged[d.date] = { date: d.date };
    merged[d.date].gas = d.price;
  });
  return Object.values(merged)
    .filter(d => d.elec != null && d.gas != null)
    .sort((a, b) => a.date.localeCompare(b.date));
}

// Split data into solid/dotted series with bridge points at transitions
function splitSeries(data, isOfficialFn) {
  const result = data.map(d => ({
    ...d,
    solidPrice: isOfficialFn(d) ? d.price : null,
    dottedPrice: !isOfficialFn(d) ? d.price : null,
  }));
  // Bridge transitions for visual continuity
  for (let i = 0; i < result.length - 1; i++) {
    if (result[i].solidPrice != null && result[i + 1].dottedPrice != null) {
      result[i].dottedPrice = result[i].solidPrice;
    }
    if (result[i].dottedPrice != null && result[i + 1].solidPrice != null) {
      result[i].solidPrice = result[i].dottedPrice;
    }
  }
  return result;
}

// Aggregate daily data to weekly averages (ISO weeks, Monday start)
function aggregateToWeekly(data, fields = ['price', 'solidPrice', 'dottedPrice']) {
  if (!data || data.length === 0) return data;
  const byWeek = {};
  data.forEach(d => {
    const date = new Date(d.date);
    const day = date.getDay();
    const diff = date.getDate() - day + (day === 0 ? -6 : 1);
    const monday = new Date(date);
    monday.setDate(diff);
    const weekKey = monday.toISOString().split('T')[0];
    if (!byWeek[weekKey]) byWeek[weekKey] = [];
    byWeek[weekKey].push(d);
  });
  const avg = (arr, key) => {
    const vals = arr.map(d => d[key]).filter(v => v != null);
    return vals.length > 0 ? +(vals.reduce((a, b) => a + b, 0) / vals.length).toFixed(2) : null;
  };
  return Object.entries(byWeek).map(([weekDate, points]) => {
    const result = { date: weekDate };
    fields.forEach(f => { result[f] = avg(points, f); });
    // Preserve metadata from last point in week
    const last = points[points.length - 1];
    if ('source' in last) result.source = last.source;
    if ('lccc' in last) result.lccc = points.some(p => p.lccc);
    if ('realPrice' in last) result.realPrice = avg(points, 'realPrice');
    return result;
  }).sort((a, b) => a.date.localeCompare(b.date));
}

function getEventLines(data) {
  if (!data || data.length === 0) return [];
  const minDate = data[0].date;
  const maxDate = data[data.length - 1].date;
  const dates = data.map(d => d.date);
  return EVENT_LINES
    .filter(e => e.date >= minDate && e.date <= maxDate)
    .map(e => {
      // Snap to nearest data point date (needed after weekly aggregation)
      let closest = dates[0];
      let minDiff = Infinity;
      for (const d of dates) {
        const diff = Math.abs(new Date(d) - new Date(e.date));
        if (diff < minDiff) { minDiff = diff; closest = d; }
      }
      return { ...e, date: closest };
    });
}

// Collision-avoidance label layer for CfD / average reference lines
// labels: [{ key, price, color, text }]
const ReferenceLabels = ({ labels, yAxisMap, xAxisMap }) => {
  const yAxis = yAxisMap && Object.values(yAxisMap)[0];
  const xAxis = xAxisMap && Object.values(xAxisMap)[0];
  if (!yAxis || !xAxis || !labels?.length) return null;

  const scale = yAxis.scale;
  const rightEdge = xAxis.x + xAxis.width + 6;
  const minGap = 14; // minimum px between label baselines

  // Map labels to pixel positions and sort top-to-bottom (low Y = top of chart)
  const positioned = labels.map(l => ({
    ...l,
    py: scale(l.price),
  })).sort((a, b) => a.py - b.py);

  // Nudge overlapping labels downward
  for (let i = 1; i < positioned.length; i++) {
    const gap = positioned[i].py - positioned[i - 1].py;
    if (gap < minGap) {
      positioned[i].py = positioned[i - 1].py + minGap;
    }
  }

  // If last label got pushed below chart, shift all upward
  const maxY = yAxis.y + yAxis.height;
  if (positioned.length && positioned[positioned.length - 1].py > maxY) {
    const overflow = positioned[positioned.length - 1].py - maxY;
    for (const p of positioned) p.py -= overflow;
    // Re-enforce minimum gaps from top
    for (let i = 1; i < positioned.length; i++) {
      if (positioned[i].py - positioned[i - 1].py < minGap) {
        positioned[i].py = positioned[i - 1].py + minGap;
      }
    }
  }

  return (
    <g>
      {positioned.map(l => {
        const actualY = scale(l.price);
        const labelY = l.py;
        return (
          <g key={l.key}>
            {Math.abs(labelY - actualY) > 2 && (
              <line x1={rightEdge - 6} y1={actualY} x2={rightEdge} y2={labelY} stroke={l.color} strokeWidth={0.8} opacity={0.5} />
            )}
            <text x={rightEdge + 2} y={labelY} fill={l.color} fontSize={11} fontWeight={600} dominantBaseline="central">
              {l.text}
            </text>
          </g>
        );
      })}
    </g>
  );
};

// Custom vertical line cursor for charts
const VerticalCursor = ({ points, height }) => {
  if (!points || !points.length) return null;
  const x = points[0].x;
  return (
    <line
      x1={x} y1={0} x2={x} y2={height}
      stroke="#333"
      strokeWidth={1}
      strokeDasharray="none"
    />
  );
};

const ElecTooltip = ({ active, payload, label, onSourceChange }) => {
  const point = payload?.[0]?.payload;
  React.useEffect(() => {
    if (active && point) {
      onSourceChange(point.source);
    }
  }, [active, point, onSourceChange]);

  if (!active || !payload?.length) return null;
  const price = point?.solidPrice ?? point?.dottedPrice;
  if (price == null) return null;
  const isDotted = point?.solidPrice == null && point?.dottedPrice != null;
  return (
    <div className="wh-tooltip">
      <p className="wh-tooltip-date">{formatDateLong(label)}</p>
      <p style={{ color: '#a8c8ff', fontWeight: 600 }}>£{Math.round(price)}/MWh</p>
      {isDotted && <p style={{ color: '#888', fontSize: '0.85em', margin: '2px 0 0' }}>Intraday approximation</p>}
    </div>
  );
};

const GasTooltip = ({ active, payload, label, unit }) => {
  if (!active || !payload?.length) return null;
  const point = payload?.[0]?.payload;
  const price = point?.solidPrice ?? point?.dottedPrice;
  if (price == null) return null;
  const isDotted = point?.solidPrice == null && point?.dottedPrice != null;
  const fmt = unit === 'ptherm'
    ? `${Math.round(price)} p/therm`
    : `£${Math.round(price)}/MWh`;
  return (
    <div className="wh-tooltip">
      <p className="wh-tooltip-date">{formatDateLong(label)}</p>
      <p style={{ color: '#f07856', fontWeight: 600 }}>{fmt}</p>
      {isDotted && <p style={{ color: '#888', fontSize: '0.85em', margin: '2px 0 0' }}>Index approximation</p>}
    </div>
  );
};

const LinkageTooltip = ({ active, payload, label }) => {
  if (!active || !payload || payload.length === 0) return null;
  return (
    <div className="wh-tooltip">
      <p className="wh-tooltip-date">{formatDateLong(label)}</p>
      {payload.filter(p => p.value != null).map((p, i) => (
        <p key={i} style={{ color: p.color, fontWeight: 600 }}>
          {p.name}: £{Math.round(p.value)}/MWh
        </p>
      ))}
    </div>
  );
};

const ProfileTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const point = payload[0]?.payload;
  if (!point) return null;
  return (
    <div className="wh-tooltip">
      <p className="wh-tooltip-date">{label}</p>
      {point.p90 != null && <p style={{ color: 'rgba(20,96,249,0.4)' }}>P90: £{point.p90.toFixed(1)}/MWh</p>}
      {point.avg != null && <p style={{ color: '#1460F9', fontWeight: 600 }}>Profile: £{point.avg.toFixed(1)}/MWh</p>}
      {point.p10 != null && <p style={{ color: 'rgba(20,96,249,0.4)' }}>P10: £{point.p10.toFixed(1)}/MWh</p>}
    </div>
  );
};

const CfdProportionTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const point = payload[0]?.payload;
  if (!point || point.proportion == null) return null;
  return (
    <div className="wh-tooltip">
      <p className="wh-tooltip-date">{formatDateLong(label)}</p>
      <p style={{ color: '#58d68d', fontWeight: 600 }}>{(point.proportion * 100).toFixed(1)}% CfD generation</p>
    </div>
  );
};

function TimeRangeSelector({ selected, onChange, loading, style }) {
  const [showCustom, setShowCustom] = useState(false);
  const [customStart, setCustomStart] = useState(selected.startDate || '');
  const [customEnd, setCustomEnd] = useState(selected.endDate || '');
  const pickerRef = useRef(null);

  // Close picker when clicking outside
  useEffect(() => {
    if (!showCustom) return;
    const handleClick = (e) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target)) setShowCustom(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [showCustom]);

  return (
    <div className="wh-time-range" style={{ ...style, position: 'relative' }}>
      {TIME_RANGES.map(r => (
        <button
          key={r.key}
          className={`wh-range-btn ${selected.key === r.key ? 'active' : ''}`}
          onClick={() => {
            if (r.key === 'custom') {
              setShowCustom(!showCustom);
              if (selected.key !== 'custom') {
                onChange({ key: 'custom', days: null, startDate: customStart, endDate: customEnd });
              }
            } else {
              setShowCustom(false);
              onChange(r);
            }
          }}
          disabled={loading && r.key === 'all'}
        >
          {r.label}
          {loading && r.key === 'all' && <span className="wh-loading-dot" />}
        </button>
      ))}
      {showCustom && (
        <div className="wh-custom-picker" ref={pickerRef}>
          <label>
            <span>From</span>
            <input type="date" value={customStart} onChange={e => setCustomStart(e.target.value)} />
          </label>
          <label>
            <span>To</span>
            <input type="date" value={customEnd} onChange={e => setCustomEnd(e.target.value)} />
          </label>
          <button className="wh-custom-apply" onClick={() => {
            if (customStart) {
              onChange({ key: 'custom', days: null, startDate: customStart, endDate: customEnd || undefined });
              setShowCustom(false);
            }
          }}>Apply</button>
        </div>
      )}
    </div>
  );
}

export default function Wholesale() {
  const [data, setData] = useState(null);
  const [allData, setAllData] = useState(null);
  const [futuresData, setFuturesData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingAll, setLoadingAll] = useState(false);
  const [error, setError] = useState(null);
  const [elecRange, setElecRange] = useState(DEFAULT_RANGE);
  const [gasRange, setGasRange] = useState(DEFAULT_RANGE);
  const [elecMode, setElecMode] = useState(null); // null | 'profile' | 'cfd' | 'averages'
  const gasMode = null; // averages removed for now
  const [priceMode, setPriceMode] = useState('nominal'); // 'nominal' | 'real'
  const [gasPriceMode, setGasPriceMode] = useState('nominal'); // independent gas toggle
  const [gasUnit, setGasUnit] = useState('mwh'); // 'mwh' | 'ptherm'
  const [profileRange, setProfileRange] = useState('1w');
  const [bandsRange, setBandsRange] = useState('5y');
  const [profileSeason, setProfileSeason] = useState('all');
  const [bandsSeason, setBandsSeason] = useState('all');
  const [elecGranularity, setElecGranularity] = useState('daily'); // 'daily' | 'hourly'
  const [, setElecSource] = useState('LCCC IMRP');
  const [cfdTechVisible, setCfdTechVisible] = useState({
    offshoreWind: true, onshoreWind: false, solar: false, nuclear: false,
  });
  const allFetched = useRef(false);
  const defaultElecSource = useRef('LCCC IMRP');

  useEffect(() => {
    document.title = 'GB energy wholesale prices | electricitybills.uk';
  }, []);

  useEffect(() => {
    fetch('/api/wholesale')
      .then(res => {
        if (!res.ok) throw new Error('Failed to fetch data');
        return res.json();
      })
      .then(d => {
        setData(d);
        setLoading(false);
      })
      .catch(err => {
        setError(err.message);
        setLoading(false);
      });
    fetch('/api/futures')
      .then(res => res.ok ? res.json() : null)
      .then(d => setFuturesData(d))
      .catch(() => {});
  }, []);

  const fetchAll = useCallback(() => {
    if (allFetched.current || loadingAll) return;
    setLoadingAll(true);
    fetch('/api/wholesale?all=true')
      .then(res => {
        if (!res.ok) throw new Error('Failed to fetch full history');
        return res.json();
      })
      .then(d => {
        setAllData(d);
        setLoadingAll(false);
        allFetched.current = true;
      })
      .catch(() => {
        setLoadingAll(false);
      });
  }, [loadingAll]);

  const activeData = useMemo(() => {
    const needsAll = ['all', 'custom'].includes(elecRange.key)
      || ['all', 'custom'].includes(gasRange.key);
    return (needsAll && allData) ? allData : data;
  }, [data, allData, elecRange, gasRange]);

  // Omit today's data — scraper runs at 6am so today's prices are incomplete
  const today = new Date().toISOString().slice(0, 10);

  const elecDaily = useMemo(() => {
    if (!activeData?.electricity) return [];
    return combineElecData(activeData.electricity.hh, activeData.electricity.daily)
      .filter(d => d.date < today);
  }, [activeData, today]);

  const gasDaily = useMemo(() => {
    if (!activeData?.gas?.data) return [];
    return processGasDaily(activeData.gas.data)
      .filter(d => d.date < today);
  }, [activeData, today]);

  const elecFiltered = useMemo(() => filterByRange(elecDaily, elecRange), [elecDaily, elecRange]);

  // Hourly electricity data — average HH pairs to hourly, with datetime key
  const elecHourly = useMemo(() => {
    const hh = activeData?.electricity?.hh;
    if (!hh || hh.length === 0) return [];
    // Group by date+hour
    const byHour = {};
    hh.forEach(row => {
      if (!row.date || !row.sp) return;
      const hour = Math.floor((row.sp - 1) / 2);
      const key = `${row.date}T${String(hour).padStart(2, '0')}:00`;
      if (!byHour[key]) byHour[key] = { prices: [], lcccPrices: [], elexonPrices: [] };
      const price = row.lccc ?? row.elexon;
      if (price != null) byHour[key].prices.push(price);
      if (row.lccc != null) byHour[key].lcccPrices.push(row.lccc);
      if (row.elexon != null) byHour[key].elexonPrices.push(row.elexon);
    });
    return Object.entries(byHour)
      .map(([datetime, { prices, lcccPrices }]) => ({
        date: datetime,
        price: prices.length > 0 ? +(prices.reduce((a, b) => a + b, 0) / prices.length).toFixed(2) : null,
        lccc: lcccPrices.length > 0 ? true : false,
      }))
      .filter(d => d.price != null)
      .sort((a, b) => a.date.localeCompare(b.date));
  }, [activeData]);

  const elecHourlyFiltered = useMemo(() => {
    if (!elecHourly.length) return [];
    return filterByRange(elecHourly, elecRange);
  }, [elecHourly, elecRange]);

  // Whether hourly data is available for this range
  const hourlyAvailable = useMemo(() => {
    return elecRange.days <= 30 || (elecRange.startDate && elecHourly.length > 0);
  }, [elecRange, elecHourly]);

  // Auto-switch granularity based on range
  const effectiveGranularity = useMemo(() => {
    if (!hourlyAvailable) return 'daily';
    if (elecRange.days <= 7) return elecGranularity === 'daily' ? 'daily' : 'hourly';
    if (elecRange.days <= 30) return elecGranularity;
    return 'daily';
  }, [hourlyAvailable, elecRange, elecGranularity]);
  const gasFiltered = useMemo(() => filterByRange(gasDaily, gasRange), [gasDaily, gasRange]);

  // Split electricity into solid (LCCC) and dotted (Elexon-only) series
  const elecChartData = useMemo(() => {
    const sourceData = effectiveGranularity === 'hourly' ? elecHourlyFiltered : elecFiltered;
    // Swap price to real if needed
    const priced = priceMode === 'real'
      ? sourceData.map(d => ({ ...d, price: d.realPrice ?? d.price }))
      : sourceData;
    const withSource = priced.map(d => ({
      ...d,
      source: d.lccc ? 'LCCC IMRP' : 'Elexon APX',
    }));
    let result = splitSeries(withSource, d => d.lccc);
    // Weekly aggregation for long ranges
    if (shouldAggregateWeekly(elecRange)) {
      result = aggregateToWeekly(result);
    }
    if (result.length > 0) {
      const latest = result[result.length - 1];
      defaultElecSource.current = latest.source || 'LCCC IMRP';
    }
    return result;
  }, [elecFiltered, elecHourlyFiltered, effectiveGranularity, priceMode, elecRange]);

  // Split gas into solid (ONS) and dotted (EEX/Smart) series
  const gasConvFactor = gasUnit === 'ptherm' ? (29.3071 / 10) : 1; // £/MWh → p/therm
  const gasChartData = useMemo(() => {
    // Swap price to real if needed
    const priced = gasPriceMode === 'real'
      ? gasFiltered.map(d => ({ ...d, price: d.realPrice ?? d.price }))
      : gasFiltered;
    const withSource = priced.map(d => ({
      ...d,
      source: d.ons != null ? 'ONS SAP' : (d.eex != null ? 'EEX' : 'Smart Energy'),
    }));
    let result = splitSeries(withSource, d => d.ons != null);
    if (shouldAggregateWeekly(gasRange)) {
      result = aggregateToWeekly(result);
    }
    // Apply unit conversion last, after all processing
    if (gasConvFactor !== 1) {
      result = result.map(d => ({
        ...d,
        price: d.price != null ? d.price * gasConvFactor : d.price,
        solidPrice: d.solidPrice != null ? d.solidPrice * gasConvFactor : d.solidPrice,
        dottedPrice: d.dottedPrice != null ? d.dottedPrice * gasConvFactor : d.dottedPrice,
      }));
    }
    return result;
  }, [gasFiltered, gasRange, gasPriceMode, gasConvFactor]);


  // Linkage: fixed range Feb 1 to today, with pre/post war averages
  const LINKAGE_START = '2026-02-01';
  const LINKAGE_WAR = '2026-02-28';

  const linkageStats = useMemo(() => {
    const merged = mergeForOverlay(elecDaily, gasDaily);
    const filtered = merged.filter(d => d.date >= LINKAGE_START);
    if (filtered.length < 2) return null;
    const preWar = filtered.filter(d => d.date < LINKAGE_WAR);
    const postWar = filtered.filter(d => d.date >= LINKAGE_WAR);
    const avg = (arr, key) => arr.length > 0 ? arr.reduce((s, d) => s + d[key], 0) / arr.length : null;
    const elecPre = avg(preWar, 'elec');
    const elecPost = avg(postWar, 'elec');
    const gasPre = avg(preWar, 'gas');
    const gasPost = avg(postWar, 'gas');
    if (!elecPre || !elecPost || !gasPre || !gasPost) return null;
    return {
      elecPre, elecPost, gasPre, gasPost,
      elecChange: ((elecPost - elecPre) / elecPre * 100),
      gasChange: ((gasPost - gasPre) / gasPre * 100),
    };
  }, [elecDaily, gasDaily]);

  // Overlay data with flat average lines baked in
  const overlayData = useMemo(() => {
    const merged = mergeForOverlay(elecDaily, gasDaily);
    const filtered = merged.filter(d => d.date >= LINKAGE_START);
    if (!linkageStats) return filtered;
    return filtered.map(d => ({
      ...d,
      elecAvg: d.date < LINKAGE_WAR ? linkageStats.elecPre : linkageStats.elecPost,
      gasAvg: d.date < LINKAGE_WAR ? linkageStats.gasPre : linkageStats.gasPost,
    }));
  }, [elecDaily, gasDaily, linkageStats]);

  const handleRangeChange = useCallback((setter) => (r) => {
    setter(r);
    if ((r.key === 'all' || r.key === 'custom') && !allFetched.current) fetchAll();
    // Auto-switch to hourly for short ranges, daily for longer
    if (setter === setElecRange) {
      if (r.days && r.days <= 7) setElecGranularity('hourly');
      else setElecGranularity('daily');
    }
  }, [fetchAll]);


  const handleElecSourceChange = useCallback((source) => {
    setElecSource(source);
  }, []);


  // Daily profile chart data
  const profileData = useMemo(() => {
    const profile = activeData?.profile;
    if (!profile) return [];
    const lineKey = `${profileRange}|${profileSeason}`;
    const bandKey = `${bandsRange}|${bandsSeason}`;
    const lineData = profile[lineKey] || [];
    const bandData = profile[bandKey] || [];

    const isReal = priceMode === 'real';
    return Array.from({ length: 24 }, (_, i) => {
      const line = lineData.find(d => d.hour === i);
      const band = bandData.find(d => d.hour === i);
      return {
        hour: i,
        time: `${String(i).padStart(2, '0')}:00`,
        avg: isReal ? (line?.realAvg ?? line?.avg ?? null) : (line?.avg ?? null),
        p10: isReal ? (band?.realP10 ?? band?.p10 ?? null) : (band?.p10 ?? null),
        p90: isReal ? (band?.realP90 ?? band?.p90 ?? null) : (band?.p90 ?? null),
      };
    });
  }, [activeData, profileRange, bandsRange, profileSeason, bandsSeason, priceMode]);

  // CfD proportion data
  const cfdProportionData = useMemo(() => {
    if (!activeData?.cfdDemand) return [];
    return [...activeData.cfdDemand].sort((a, b) => a.date.localeCompare(b.date));
  }, [activeData]);

  const cfdProportionFiltered = useMemo(
    () => filterByRange(cfdProportionData, elecRange),
    [cfdProportionData, elecRange]
  );

  // Align CfD proportion sub-chart to use the same x-axis dates as the main chart
  const cfdProportionAligned = useMemo(() => {
    if (!cfdProportionFiltered.length || !elecChartData.length) return cfdProportionFiltered;
    // Build lookup from CfD data
    const cfdByDate = {};
    cfdProportionFiltered.forEach(d => { cfdByDate[d.date] = d.proportion; });
    // Use electricity chart's date set, filling in CfD values where available
    return elecChartData.map(d => ({
      date: d.date,
      proportion: cfdByDate[d.date] ?? null,
    }));
  }, [cfdProportionFiltered, elecChartData]);

  // CfD strike prices — stepped in nominal mode, flat in real mode
  const cfdAuctions = useMemo(() => {
    const cpiData = activeData?.cpiData;
    if (!cpiData?.latestCpi) return [];
    const auctions = [];
    for (const [tech, entries] of Object.entries(CFD_STRIKE_PRICES)) {
      if (!cfdTechVisible[tech]) continue;
      for (const entry of entries) {
        // Real price: price2025 adjusted to current CPI (price2025 is Apr 2025 value using Jan 2025 CPI)
        const jan2025 = cpiData.monthly?.['2025-01'] || 135.4;
        const realPrice = Math.round(entry.price2025 * cpiData.latestCpi / jan2025 * 100) / 100;
        auctions.push({
          name: `${entry.round} ${CFD_TECH_LABELS[tech]}`,
          round: entry.round,
          tech,
          price2012: entry.price2012,
          price2025: entry.price2025,
          awardYear: entry.awardYear,
          realPrice,
          color: CFD_TECH_COLORS[tech],
        });
      }
    }
    return auctions;
  }, [activeData, cfdTechVisible]);

  // For nominal mode: compute the CfD price for a given date
  // Known anchor: price2025 applies from April 2025 (uses Jan 2025 CPI).
  // For other years: scale from price2025 using the ratio of January CPIs.
  const getCfdNominalPrice = useCallback((cfd, dateStr) => {
    const cpiData = activeData?.cpiData;
    if (!cpiData?.monthly) return null;
    const d = new Date(dateStr + 'T00:00:00Z');
    const month = d.getUTCMonth();
    const year = d.getUTCFullYear();
    // Contract year: Apr Y → Mar Y+1
    const contractYear = month < 3 ? year - 1 : year;
    if (contractYear === 2025) return cfd.price2025; // known value
    // Scale from the known 2025 value using CPI ratio
    const janCpi = cpiData.monthly[`${contractYear}-01`];
    const jan2025 = cpiData.monthly['2025-01'];
    if (janCpi == null || jan2025 == null) return null;
    return Math.round(cfd.price2025 * janCpi / jan2025 * 100) / 100;
  }, [activeData]);

  // Inject CfD columns into chart data for stepped rendering
  const elecChartDataWithCfd = useMemo(() => {
    if (elecMode !== 'cfd' || priceMode === 'real' || cfdAuctions.length === 0) return elecChartData;
    return elecChartData.map(d => {
      const row = { ...d };
      for (const cfd of cfdAuctions) {
        if (d.date < `${cfd.awardYear}-04-01`) continue;
        const price = getCfdNominalPrice(cfd, d.date);
        if (price != null) row[`cfd_${cfd.name}`] = price;
      }
      return row;
    });
  }, [elecChartData, elecMode, priceMode, cfdAuctions, getCfdNominalPrice]);

  const elecEventLines = useMemo(() => getEventLines(elecChartData), [elecChartData]);
  const gasEventLines = useMemo(() => getEventLines(gasChartData), [gasChartData]);

  const elecAvgLabels = useMemo(() => {
    if (!activeData?.averages) return [];
    return AVERAGE_LINES.map(line => {
      const entry = activeData.averages[line.key];
      if (entry == null) return null;
      const val = priceMode === 'real' ? (entry.real ?? entry.nominal) : (entry.nominal ?? entry);
      if (val == null) return null;
      return { key: line.key, price: val, color: line.color, text: `${line.label} (£${Math.round(val)})` };
    }).filter(Boolean);
  }, [activeData, priceMode]);

  const cfdLabels = useMemo(() => {
    if (priceMode === 'real') {
      return cfdAuctions.map(c => ({
        key: c.name, price: c.realPrice, color: c.color,
        text: `${c.round} (£${Math.round(c.realPrice)})`,
      }));
    }
    // Nominal: use the latest data point's CfD price for label positioning
    const lastRow = elecChartDataWithCfd[elecChartDataWithCfd.length - 1];
    return cfdAuctions.map(c => {
      const price = lastRow?.[`cfd_${c.name}`] ?? c.price2025;
      return {
        key: c.name, price, color: c.color,
        text: `${c.round} (£${Math.round(price)})`,
      };
    });
  }, [cfdAuctions, priceMode, elecChartDataWithCfd]);

  if (loading) {
    return (
      <div className="App">
        <div className="wh-page">
          <div className="wh-loading">Loading wholesale data...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="App">
        <div className="wh-page">
          <div className="wh-error">Error: {error}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="App">
      <div className="wh-page">
        {/* Electricity Section */}
        <section className="wh-section">
          <div className="wh-section-header">
            <h2>Electricity Spot Price</h2>
            <span className="wh-section-subtitle">Day-ahead wholesale prices</span>
          </div>

          <div className="wh-chart-card">
            <div className="wh-chart-controls">
              <div className="wh-controls-row">
                <div className="wh-left-controls">
                  {elecMode === 'profile' ? (
                    <div className="wh-profile-grid">
                      <label>
                        <span className="wh-profile-label" style={{ color: '#1460F9' }}>PROFILE AVG</span>
                        <select value={profileRange} onChange={e => setProfileRange(e.target.value)} className="wh-profile-select wh-profile-select-sm">
                          {PROFILE_RANGE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.shortLabel || o.label}</option>)}
                        </select>
                      </label>
                      <label>
                        <span className="wh-profile-label" style={{ color: 'rgba(20,96,249,0.45)' }}>BAND P10/90</span>
                        <select value={bandsRange} onChange={e => setBandsRange(e.target.value)} className="wh-profile-select wh-profile-select-sm">
                          {PROFILE_RANGE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.shortLabel || o.label}</option>)}
                        </select>
                      </label>
                      <label>
                        <span className="wh-profile-label" style={{ color: '#1460F9' }}>Season</span>
                        <select
                          value={profileSeason}
                          onChange={e => setProfileSeason(e.target.value)}
                          className="wh-profile-select wh-profile-select-sm"
                          disabled={!['1y', '5y', 'noncrisis'].includes(profileRange)}
                        >
                          {SEASON_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </select>
                      </label>
                      <label>
                        <span className="wh-profile-label" style={{ color: 'rgba(20,96,249,0.45)' }}>Season</span>
                        <select
                          value={bandsSeason}
                          onChange={e => setBandsSeason(e.target.value)}
                          className="wh-profile-select wh-profile-select-sm"
                          disabled={!['1y', '5y', 'noncrisis'].includes(bandsRange)}
                        >
                          {SEASON_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </select>
                      </label>
                    </div>
                  ) : (
                    <TimeRangeSelector
                      selected={elecRange}
                      onChange={handleRangeChange(setElecRange)}
                      loading={loadingAll}
                    />
                  )}
                  <div className="wh-secondary-controls">
                    <div className="wh-price-mode-toggle">
                      <button
                        className={`wh-range-btn ${priceMode === 'nominal' ? 'active' : ''}`}
                        onClick={() => setPriceMode('nominal')}
                      >Nominal</button>
                      <button
                        className={`wh-range-btn ${priceMode === 'real' ? 'active' : ''}`}
                        onClick={() => setPriceMode('real')}
                      >Real £2026</button>
                    </div>
                    {hourlyAvailable && elecMode !== 'profile' && (
                      <div className="wh-granularity-toggle">
                        <button
                          className={`wh-range-btn ${effectiveGranularity === 'daily' ? 'active' : ''}`}
                          onClick={() => setElecGranularity('daily')}
                        >Daily</button>
                        <button
                          className={`wh-range-btn ${effectiveGranularity === 'hourly' ? 'active' : ''}`}
                          onClick={() => setElecGranularity('hourly')}
                        >Hourly</button>
                      </div>
                    )}
                  </div>
                </div>
                <div className="wh-mode-selector">
                  <span className="wh-mode-label">View</span>
                  {[
                    { key: 'cfd', label: 'CfD strikes' },
                    { key: 'profile', label: 'Daily Profile' },
                  ].map(m => (
                    <button
                      key={m.key}
                      className={`wh-toggle-btn ${elecMode === m.key ? 'active' : ''}`}
                      onClick={() => {
                        const newMode = elecMode === m.key ? null : m.key;
                        setElecMode(newMode);
                        if (newMode === 'cfd' && !allFetched.current) {
                          fetchAll();
                        }
                      }}
                    >
                      {m.label}
                    </button>
                  ))}
                  <a
                    href={GOOGLE_SHEET_URL}
                    target="_blank"
                    rel="noreferrer"
                    className="wh-download-btn"
                    title="Open data in Google Sheets"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                      <polyline points="7 10 12 15 17 10" />
                      <line x1="12" y1="15" x2="12" y2="3" />
                    </svg>
                  </a>
                </div>
              </div>
              {elecMode === 'cfd' && (
                <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', justifyContent: 'center' }}>
                  {Object.entries(CFD_TECH_LABELS).map(([tech, label]) => (
                    <button
                      key={tech}
                      className={`wh-toggle-btn ${cfdTechVisible[tech] ? 'active' : ''}`}
                      style={cfdTechVisible[tech]
                        ? { fontSize: '0.75em', color: CFD_TECH_COLORS[tech], borderColor: CFD_TECH_COLORS[tech], background: `${CFD_TECH_COLORS[tech]}14` }
                        : { fontSize: '0.75em' }}
                      onClick={() => setCfdTechVisible(prev => ({ ...prev, [tech]: !prev[tech] }))}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Daily Profile Mode */}
            {elecMode === 'profile' && (
              <>
                <div className="wh-chart-container" key="profile">
                  <ResponsiveContainer width="100%" height={400}>
                    <ComposedChart data={profileData} margin={{ top: 10, right: 20, left: 10, bottom: 0 }}>
                      <defs>
                        <linearGradient id="bandGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#1460F9" stopOpacity={0.1} />
                          <stop offset="100%" stopColor="#1460F9" stopOpacity={0.05} />
                        </linearGradient>
                      </defs>
                      <XAxis
                        dataKey="time"
                        tick={{ fill: AXIS_TICK_COLOR, fontSize: 12 }}
                        axisLine={{ stroke: '#ddd' }}
                        tickLine={false}
                        interval={2}
                      />
                      <YAxis
                        tick={{ fill: AXIS_TICK_COLOR, fontSize: 12 }}
                        axisLine={{ stroke: '#eee' }}
                        tickLine={false}
                        tickFormatter={v => `£${v}`}
                        width={65}
                        domain={[0, 'auto']}
                        allowDataOverflow={true}
                        label={{ value: '£/MWh', angle: -90, position: 'insideLeft', offset: 5, style: { fill: '#888', fontSize: 12 } }}
                      />
                      <Tooltip content={<ProfileTooltip />} cursor={<VerticalCursor />} position={{ y: 0 }} />
                      {/* P90 band (upper) */}
                      <Area
                        type="monotone"
                        dataKey="p90"
                        stroke="rgba(20,96,249,0.3)"
                        strokeWidth={1}
                        fill="url(#bandGradient)"
                        dot={false}
                        activeDot={false}
                        isAnimationActive={false}
                      />
                      {/* P10 band (lower) — white fill cuts out the area below */}
                      <Area
                        type="monotone"
                        dataKey="p10"
                        stroke="rgba(20,96,249,0.3)"
                        strokeWidth={1}
                        fill="white"
                        dot={false}
                        activeDot={false}
                        isAnimationActive={false}
                      />
                      {/* Main profile line */}
                      <Line
                        type="monotone"
                        dataKey="avg"
                        stroke="#1460F9"
                        strokeWidth={2.5}
                        dot={false}
                        activeDot={{ r: 4, fill: '#1460F9' }}
                        isAnimationActive={false}
                      />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
                <div className="wh-source-text">
                  Source: LCCC IMRP and EPEX via Elexon.{' '}
                  <a href={GOOGLE_SHEET_URL} target="_blank" rel="noreferrer" className="wh-data-link">
                    Data <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ verticalAlign: '-1px' }}><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" /><polyline points="15 3 21 3 21 9" /><line x1="10" y1="14" x2="21" y2="3" /></svg>
                  </a>
                </div>
              </>
            )}

            {/* Standard chart (non-profile modes) */}
            {elecMode !== 'profile' && (
              <>
                <div className="wh-chart-container">
                  <ResponsiveContainer width="100%" height={400}>
                    <AreaChart
                      data={elecMode === 'cfd' ? elecChartDataWithCfd : elecChartData}
                      margin={{ top: 10, right: elecMode === 'cfd' ? 120 : 20, left: 10, bottom: 0 }}
                      onMouseLeave={() => setElecSource(defaultElecSource.current)}
                      syncId={elecMode === 'cfd' ? 'elecCfd' : undefined}
                    >
                      <defs>
                        <linearGradient id="elecGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#1460F9" stopOpacity={0.3} />
                          <stop offset="95%" stopColor="#1460F9" stopOpacity={0.05} />
                        </linearGradient>
                      </defs>
                      <XAxis
                        dataKey="date"
                        tickFormatter={formatDate}
                        tick={{ fill: AXIS_TICK_COLOR, fontSize: 12 }}
                        axisLine={{ stroke: '#ddd' }}
                        tickLine={false}
                        minTickGap={40}
                      />
                      <YAxis
                        tick={{ fill: AXIS_TICK_COLOR, fontSize: 12 }}
                        axisLine={{ stroke: '#eee' }}
                        tickLine={false}
                        tickFormatter={v => `£${v}`}
                        width={65}
                        domain={[0, elecMode === 'cfd' && cfdLabels.length > 0
                          ? (dataMax) => {
                              const maxCfd = Math.max(...cfdLabels.map(c => c.price));
                              return Math.ceil(Math.max(dataMax, maxCfd) * 1.05 / 5) * 5;
                            }
                          : 'auto']}
                        allowDataOverflow={true}
                        label={{ value: '£/MWh', angle: -90, position: 'insideLeft', offset: 5, style: { fill: '#888', fontSize: 12 } }}
                      />
                      <Tooltip content={<ElecTooltip onSourceChange={handleElecSourceChange} />} cursor={<VerticalCursor />} position={{ y: 0 }} />
                      <Area
                        type="linear"
                        dataKey="price"
                        stroke="transparent"
                        strokeWidth={0}
                        fill="url(#elecGradient)"
                        dot={false}
                        activeDot={false}
                        isAnimationActive={false}
                      />
                      <Area
                        type="linear"
                        dataKey="solidPrice"
                        stroke="#1460F9"
                        strokeWidth={2.5}
                        fill="transparent"
                        dot={false}
                        activeDot={{ r: 4, fill: '#1460F9' }}
                        connectNulls={false}
                        isAnimationActive={false}
                      />
                      <Area
                        type="linear"
                        dataKey="dottedPrice"
                        stroke="#1460F9"
                        strokeWidth={2.5}
                        strokeDasharray="4 3"
                        fill="transparent"
                        dot={false}
                        activeDot={{ r: 4, fill: '#1460F9' }}
                        connectNulls={false}
                        isAnimationActive={false}
                      />
                      {elecEventLines.map(e => (
                        <ReferenceLine
                          key={e.date}
                          x={e.date}
                          stroke="#aaa"
                          strokeDasharray="6 4"
                          strokeWidth={1}
                          label={{ value: e.label, position: 'insideTopLeft', fill: AXIS_TICK_COLOR, fontSize: EVENT_LABEL_SIZE }}
                        />
                      ))}
                      {/* CfD strike prices: stepped lines (nominal) or flat lines (real) */}
                      {elecMode === 'cfd' && priceMode === 'real' && cfdAuctions.map((cfd) => (
                        <ReferenceLine
                          key={cfd.name}
                          y={cfd.realPrice}
                          stroke={cfd.color}
                          strokeDasharray="6 4"
                          strokeWidth={1.5}
                        />
                      ))}
                      {elecMode === 'cfd' && priceMode !== 'real' && cfdAuctions.map((cfd) => (
                        <Area
                          key={`cfd_${cfd.name}`}
                          type="stepAfter"
                          dataKey={`cfd_${cfd.name}`}
                          stroke={cfd.color}
                          strokeDasharray="6 4"
                          strokeWidth={1.5}
                          fill="transparent"
                          dot={false}
                          activeDot={false}
                          connectNulls={false}
                          isAnimationActive={false}
                        />
                      ))}
                      {elecMode === 'cfd' && cfdLabels.length > 0 && (
                        <Customized component={(props) => <ReferenceLabels {...props} labels={cfdLabels} />} />
                      )}
                      {elecMode === 'averages' && elecAvgLabels.map(l => (
                        <ReferenceLine key={l.key} y={l.price} stroke={l.color} strokeDasharray="8 4" strokeWidth={1.5} />
                      ))}
                      {elecMode === 'averages' && elecAvgLabels.length > 0 && (
                        <Customized component={(props) => <ReferenceLabels {...props} labels={elecAvgLabels} />} />
                      )}
                    </AreaChart>
                  </ResponsiveContainer>
                </div>

                {/* CfD proportion sub-chart */}
                {elecMode === 'cfd' && cfdProportionAligned.length > 0 && (
                  <div className="wh-chart-container wh-subchart">
                    <ResponsiveContainer width="100%" height={200}>
                      <AreaChart
                        data={cfdProportionAligned}
                        margin={{ top: 5, right: 120, left: 10, bottom: 0 }}
                        syncId="elecCfd"
                      >
                        <defs>
                          <linearGradient id="cfdGradient" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#58d68d" stopOpacity={0.3} />
                            <stop offset="95%" stopColor="#58d68d" stopOpacity={0.05} />
                          </linearGradient>
                        </defs>
                        <XAxis
                          dataKey="date"
                          tickFormatter={formatDate}
                          tick={{ fill: AXIS_TICK_COLOR, fontSize: 11 }}
                          axisLine={{ stroke: '#ddd' }}
                          tickLine={false}
                          minTickGap={40}
                        />
                        <YAxis
                          tick={{ fill: AXIS_TICK_COLOR, fontSize: 11 }}
                          axisLine={{ stroke: '#eee' }}
                          tickLine={false}
                          tickFormatter={v => `${(v * 100).toFixed(0)}%`}
                          width={65}
                          domain={[0, 'auto']}
                          label={{ value: 'CfD % GB demand', angle: -90, position: 'insideLeft', offset: 5, dy: 40, style: { fill: '#888', fontSize: 12 } }}
                        />
                        <Tooltip content={<CfdProportionTooltip />} />
                        <Area
                          type="monotone"
                          dataKey="proportion"
                          stroke="#58d68d"
                          strokeWidth={2}
                          fill="url(#cfdGradient)"
                          dot={false}
                          activeDot={{ r: 3, fill: '#58d68d' }}
                          isAnimationActive={false}
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                )}

                <div className="wh-source-text">
                  Source: LCCC IMRP and EPEX via Elexon.{' '}
                  <a href={GOOGLE_SHEET_URL} target="_blank" rel="noreferrer" className="wh-data-link">
                    Data <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ verticalAlign: '-1px' }}><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" /><polyline points="15 3 21 3 21 9" /><line x1="10" y1="14" x2="21" y2="3" /></svg>
                  </a>
                </div>
                {elecMode === 'cfd' && (
                  <div className="wh-cfd-note">
                    CfD strike prices displayed in £2026. Note: scalar CfD strike prices do not account for capture rate or system cost effects.
                  </div>
                )}
                {elecMode === 'averages' && (
                  <div className="wh-cfd-note">
                    Historical average prices across different periods
                  </div>
                )}
              </>
            )}
          </div>
        </section>

        {/* Electricity Futures Chart */}
        <FuturesChart futuresData={futuresData} />

        {/* Electricity Price Cap Prediction */}
        <PriceCap futuresData={futuresData} fuel="electricity" />

        {/* Gas Section */}
        <section className="wh-section">
          <div className="wh-section-header">
            <h2>Gas Spot Price</h2>
            <span className="wh-section-subtitle">Spot System Average Price</span>
          </div>

          <div className="wh-chart-card">
            <div className="wh-chart-controls">
              <div className="wh-controls-row">
                <div className="wh-left-controls">
                  <TimeRangeSelector selected={gasRange} onChange={handleRangeChange(setGasRange)} loading={loadingAll} />
                  <div className="wh-secondary-controls">
                    <div className="wh-price-mode-toggle">
                      <button
                        className={`wh-range-btn ${gasPriceMode === 'nominal' ? 'active' : ''}`}
                        onClick={() => setGasPriceMode('nominal')}
                      >Nominal</button>
                      <button
                        className={`wh-range-btn ${gasPriceMode === 'real' ? 'active' : ''}`}
                        onClick={() => setGasPriceMode('real')}
                      >Real £2026</button>
                    </div>
                    <div className="wh-granularity-toggle">
                      <button
                        className={`wh-range-btn ${gasUnit === 'mwh' ? 'active' : ''}`}
                        onClick={() => setGasUnit('mwh')}
                      >£/MWh</button>
                      <button
                        className={`wh-range-btn ${gasUnit === 'ptherm' ? 'active' : ''}`}
                        onClick={() => setGasUnit('ptherm')}
                      >p/therm</button>
                    </div>
                  </div>
                </div>
                <div className="wh-mode-selector">
                  <a
                    href={GOOGLE_SHEET_URL}
                    target="_blank"
                    rel="noreferrer"
                    className="wh-download-btn"
                    title="Open data in Google Sheets"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                      <polyline points="7 10 12 15 17 10" />
                      <line x1="12" y1="15" x2="12" y2="3" />
                    </svg>
                  </a>
                </div>
              </div>
            </div>
            <div className="wh-chart-container">
              <ResponsiveContainer width="100%" height={400}>
                <AreaChart
                  data={gasChartData}
                  margin={{ top: 10, right: gasMode === 'averages' ? 120 : 20, left: 10, bottom: 0 }}
                >
                  <defs>
                    <linearGradient id="gasGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#f07856" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#f07856" stopOpacity={0.05} />
                    </linearGradient>
                  </defs>
                  <XAxis
                    dataKey="date"
                    tickFormatter={formatDate}
                    tick={{ fill: AXIS_TICK_COLOR, fontSize: 12 }}
                    axisLine={{ stroke: '#ddd' }}
                    tickLine={false}
                    minTickGap={40}
                  />
                  <YAxis
                    tick={{ fill: AXIS_TICK_COLOR, fontSize: 12 }}
                    axisLine={{ stroke: '#eee' }}
                    tickLine={false}
                    tickFormatter={v => gasUnit === 'ptherm' ? `${Math.round(v)}p` : `£${v}`}
                    width={65}
                    domain={[0, 'auto']}
                    label={{ value: gasUnit === 'ptherm' ? 'p/therm' : '£/MWh', angle: -90, position: 'insideLeft', offset: 5, style: { fill: '#888', fontSize: 12 } }}
                  />
                  <Tooltip content={<GasTooltip unit={gasUnit} />} cursor={<VerticalCursor />} position={{ y: 0 }} />
                  <Area
                    type="linear"
                    dataKey="price"
                    stroke="transparent"
                    strokeWidth={0}
                    fill="url(#gasGradient)"
                    dot={false}
                    activeDot={false}
                    isAnimationActive={false}
                  />
                  <Area
                    type="linear"
                    dataKey="solidPrice"
                    stroke="#f07856"
                    strokeWidth={2.5}
                    fill="transparent"
                    dot={false}
                    activeDot={{ r: 4, fill: '#f07856' }}
                    connectNulls={false}
                    isAnimationActive={false}
                  />
                  <Area
                    type="linear"
                    dataKey="dottedPrice"
                    stroke="#f07856"
                    strokeWidth={2.5}
                    strokeDasharray="4 3"
                    fill="transparent"
                    dot={false}
                    activeDot={{ r: 4, fill: '#f07856' }}
                    connectNulls={false}
                    isAnimationActive={false}
                  />
                  {gasEventLines.map(e => (
                    <ReferenceLine
                      key={e.date}
                      x={e.date}
                      stroke="#aaa"
                      strokeDasharray="6 4"
                      strokeWidth={1}
                      label={{ value: e.label, position: 'insideTopLeft', fill: AXIS_TICK_COLOR, fontSize: EVENT_LABEL_SIZE }}
                    />
                  ))}
                  {gasMode === 'averages' && activeData?.gasAverages && (() => {
                    const avgLabels = [];
                    return (<>
                      {AVERAGE_LINES.map(line => {
                        const entry = activeData.gasAverages[line.key];
                        if (entry == null) return null;
                        const rawVal = gasPriceMode === 'real' ? (entry.real ?? entry.nominal) : (entry.nominal ?? entry);
                        if (rawVal == null) return null;
                        const val = rawVal * gasConvFactor;
                        const fmtVal = gasUnit === 'ptherm' ? `${Math.round(val)}p` : `£${Math.round(val)}`;
                        avgLabels.push({
                          key: line.key, price: val, color: line.color,
                          text: `${line.label} (${fmtVal})`,
                        });
                        return (
                          <ReferenceLine
                            key={line.key}
                            y={val}
                            stroke={line.color}
                            strokeDasharray="8 4"
                            strokeWidth={1.5}
                          />
                        );
                      })}
                      {avgLabels.length > 0 && (
                        <Customized component={(props) => <ReferenceLabels {...props} labels={avgLabels} />} />
                      )}
                    </>);
                  })()}
                </AreaChart>
              </ResponsiveContainer>
            </div>
            <div className="wh-source-text">Source: ONS, EEX, Smart Energy</div>
          </div>
        </section>

        {/* Gas Futures Chart */}
        <FuturesChart futuresData={futuresData} fuel="gas" />

        {/* Gas Price Cap Prediction */}
        <PriceCap futuresData={futuresData} fuel="gas" />

        {/* Linkage Section */}
        <section className="wh-section">
          <div className="wh-section-header">
            <h2>Gas-Electricity Linkage</h2>
            <span className="wh-section-subtitle">Impact of Iran conflict on wholesale prices</span>
          </div>

          <div className="wh-chart-card">
            <div className="wh-chart-container">
              <ResponsiveContainer width="100%" height={400}>
                <ComposedChart data={overlayData} margin={{ top: 10, right: 20, left: 10, bottom: 0 }}>
                  <defs>
                    <linearGradient id="elecOverlayGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#1460F9" stopOpacity={0.15} />
                      <stop offset="95%" stopColor="#1460F9" stopOpacity={0.01} />
                    </linearGradient>
                    <linearGradient id="gasOverlayGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#f07856" stopOpacity={0.15} />
                      <stop offset="95%" stopColor="#f07856" stopOpacity={0.01} />
                    </linearGradient>
                  </defs>
                  <XAxis
                    dataKey="date"
                    tickFormatter={formatDate}
                    tick={{ fill: AXIS_TICK_COLOR, fontSize: 12 }}
                    axisLine={{ stroke: '#ddd' }}
                    tickLine={false}
                    minTickGap={40}
                  />
                  <YAxis
                    tick={{ fill: AXIS_TICK_COLOR, fontSize: 12 }}
                    axisLine={{ stroke: '#eee' }}
                    tickLine={false}
                    tickFormatter={v => `£${v}`}
                    width={65}
                    domain={[0, 'auto']}
                    label={{ value: '£/MWh', angle: -90, position: 'insideLeft', offset: 5, style: { fill: '#888', fontSize: 12 } }}
                  />
                  <Tooltip content={<LinkageTooltip />} cursor={<VerticalCursor />} position={{ y: 0 }} />
                  <RechartsLegend
                    verticalAlign="top"
                    height={36}
                    iconType="line"
                    wrapperStyle={{ fontSize: 12, color: '#666' }}
                  />
                  <Area
                    type="monotone"
                    dataKey="elec"
                    name="Electricity"
                    stroke="#1460F9"
                    fill="url(#elecOverlayGradient)"
                    strokeWidth={2}
                    dot={false}
                    activeDot={{ r: 4, fill: '#1460F9' }}
                    isAnimationActive={false}
                  />
                  <Area
                    type="monotone"
                    dataKey="gas"
                    name="Gas"
                    stroke="#f07856"
                    fill="url(#gasOverlayGradient)"
                    strokeWidth={2}
                    dot={false}
                    activeDot={{ r: 4, fill: '#f07856' }}
                    isAnimationActive={false}
                  />
                  {/* Iran war vertical marker */}
                  {overlayData.length > 0 && (
                    <ReferenceLine
                      x={(() => {
                        const dates = overlayData.map(d => d.date);
                        let closest = dates[0];
                        let minDiff = Infinity;
                        for (const d of dates) {
                          const diff = Math.abs(new Date(d) - new Date(LINKAGE_WAR));
                          if (diff < minDiff) { minDiff = diff; closest = d; }
                        }
                        return closest;
                      })()}
                      stroke="#aaa"
                      strokeDasharray="6 4"
                      strokeWidth={1}
                      label={{ value: 'Iran war', position: 'insideTopLeft', fill: AXIS_TICK_COLOR, fontSize: EVENT_LABEL_SIZE }}
                    />
                  )}
                  {/* Pre/post war average flat lines */}
                  <Line type="stepAfter" dataKey="elecAvg" stroke="#1460F9" strokeDasharray="8 4" strokeWidth={1.5} dot={false} activeDot={false} isAnimationActive={false} legendType="none" />
                  <Line type="stepAfter" dataKey="gasAvg" stroke="#f07856" strokeDasharray="8 4" strokeWidth={1.5} dot={false} activeDot={false} isAnimationActive={false} legendType="none" />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </div>

          {linkageStats && (
            <div className="wh-stats-row" style={{ marginTop: 12 }}>
              <div className="wh-stat wh-stat-small" style={{ textAlign: 'center' }}>
                <div className="wh-stat-label" style={{ color: '#1460F9' }}>Electricity</div>
                <div style={{ display: 'flex', gap: 16, marginTop: 4, justifyContent: 'center' }}>
                  <div>
                    <div className="wh-stat-label">Pre-war avg</div>
                    <div className="wh-stat-value" style={{ fontSize: '1em' }}>£{Math.round(linkageStats.elecPre)}<span className="wh-stat-unit">/MWh</span></div>
                  </div>
                  <div>
                    <div className="wh-stat-label">Post-war avg</div>
                    <div className="wh-stat-value" style={{ fontSize: '1em' }}>£{Math.round(linkageStats.elecPost)}<span className="wh-stat-unit">/MWh</span></div>
                  </div>
                  <div>
                    <div className="wh-stat-label">Change</div>
                    <div className={`wh-stat-change ${linkageStats.elecChange > 0 ? 'up' : 'down'}`} style={{ fontSize: '1em', fontWeight: 600, marginTop: 0 }}>
                      {linkageStats.elecChange > 0 ? '+' : ''}{linkageStats.elecChange.toFixed(1)}%
                    </div>
                  </div>
                </div>
              </div>
              <div className="wh-stat wh-stat-small" style={{ textAlign: 'center' }}>
                <div className="wh-stat-label" style={{ color: '#f07856' }}>Gas</div>
                <div style={{ display: 'flex', gap: 16, marginTop: 4, justifyContent: 'center' }}>
                  <div>
                    <div className="wh-stat-label">Pre-war avg</div>
                    <div className="wh-stat-value" style={{ fontSize: '1em' }}>£{Math.round(linkageStats.gasPre)}<span className="wh-stat-unit">/MWh</span></div>
                  </div>
                  <div>
                    <div className="wh-stat-label">Post-war avg</div>
                    <div className="wh-stat-value" style={{ fontSize: '1em' }}>£{Math.round(linkageStats.gasPost)}<span className="wh-stat-unit">/MWh</span></div>
                  </div>
                  <div>
                    <div className="wh-stat-label">Change</div>
                    <div className={`wh-stat-change ${linkageStats.gasChange > 0 ? 'up' : 'down'}`} style={{ fontSize: '1em', fontWeight: 600, marginTop: 0 }}>
                      {linkageStats.gasChange > 0 ? '+' : ''}{linkageStats.gasChange.toFixed(1)}%
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </section>

        <div className="footer">
          By <a href="https://benjames.io" target="_blank" rel="noreferrer">Ben James</a>
        </div>
      </div>
    </div>
  );
}
